import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string)?.replace(/\/$/, '');
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/** User-facing message for HTTP status (do not swallow errors). */
function messageForStatus(status: number, bodyError?: string): string {
  if (status === 401) return 'Session expired, please sign in again';
  if (status === 403) return "You don't own this project";
  if (status === 500) return bodyError || 'Server error. Please try again.';
  return bodyError || `Request failed (${status})`;
}

interface EnvVar {
  id: string;
  key: string;
  created_at: string;
  updated_at: string;
}

async function callProjectEnvVars<T>(body: Record<string, unknown>): Promise<{ data?: T; status: number; errorMessage?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { status: 401, errorMessage: 'Session expired, please sign in again' };
  }
  const res = await fetch(`${SUPABASE_URL}/functions/v1/project-env-vars`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  const errMsg = data?.error ?? data?.message;
  if (!res.ok) {
    return { status: res.status, errorMessage: messageForStatus(res.status, errMsg) };
  }
  if (data?.error) {
    return { status: res.status, errorMessage: data.error };
  }
  return { data: data as T, status: res.status };
}

export function useProjectEnvVars(projectId: string | null) {
  const [envVars, setEnvVars] = useState<EnvVar[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchEnvVars = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const out = await callProjectEnvVars<{ envVars?: EnvVar[] }>({ action: 'list', projectId });
      if (out.errorMessage) {
        toast.error(out.errorMessage);
        return;
      }
      setEnvVars(out.data?.envVars ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load environment variables');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const setEnvVar = useCallback(async (key: string, value: string): Promise<boolean> => {
    if (!projectId) return false;
    setSaving(true);
    try {
      const out = await callProjectEnvVars<{ error?: string }>({ action: 'set', projectId, key, value });
      if (out.errorMessage) {
        toast.error(out.errorMessage);
        return false;
      }
      toast.success(`Environment variable ${key} saved`);
      await fetchEnvVars();
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save environment variable');
      return false;
    } finally {
      setSaving(false);
    }
  }, [projectId, fetchEnvVars]);

  const deleteEnvVar = useCallback(async (key: string): Promise<boolean> => {
    if (!projectId) return false;
    try {
      const out = await callProjectEnvVars<{ error?: string }>({ action: 'delete', projectId, key });
      if (out.errorMessage) {
        toast.error(out.errorMessage);
        return false;
      }
      toast.success(`Environment variable ${key} deleted`);
      await fetchEnvVars();
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete environment variable');
      return false;
    }
  }, [projectId, fetchEnvVars]);

  return {
    envVars,
    loading,
    saving,
    fetchEnvVars,
    setEnvVar,
    deleteEnvVar,
  };
}
